// Wikimedia's image addresses (upload.wikimedia.org, and thumb.wikimedia.org, which its API gives out
// now), where most web photos of places come from: an original (/wikipedia/commons/1/16/Name.jpg) and its
// thumbnails (/wikipedia/commons/thumb/1/16/Name.jpg/960px-Name.jpg), maybe with tracking parameters after.
// Other sites may only load its standard thumbnail widths; any other answers 400.
// https://www.mediawiki.org/wiki/Common_thumbnail_sizes. Shared by the check (web-photos.ts) and the
// web view, which loads a smaller one where a photo shows small.

export const WIKIMEDIA_WIDTHS = [20, 40, 60, 120, 250, 330, 500, 960, 1280, 1920, 3840] as const;

const ORIGINAL =
  /^https:\/\/(upload|thumb)\.wikimedia\.org\/wikipedia\/([^/]+)\/([0-9a-f])\/([0-9a-f]{2})\/([^/?#]+)(\?[^#]*)?$/;
const THUMB =
  /^https:\/\/(upload|thumb)\.wikimedia\.org\/wikipedia\/([^/]+)\/thumb\/([0-9a-f])\/([0-9a-f]{2})\/([^/?#]+)\/(\d+)px-([^/?#]+)(\?[^#]*)?$/;
// Thumbnails come in the original's own type for these; others (SVG, PDF, TIFF) differ.
const PHOTO = /\.(jpe?g|png|webp|gif)$/i;

export interface WikimediaImage {
  host: string;
  // The original's project ("commons") and its path under it.
  project: string;
  path: string;
  name: string;
  // The thumbnail's width; null for the original.
  width: number | null;
}

export function wikimediaImage(url: string): WikimediaImage | null {
  const thumb = url.match(THUMB);
  if (thumb) {
    const [, host, project, a, ab, name, width, thumbName] = thumb as unknown as string[];
    if (thumbName !== name) return null;
    return {
      host: `${host}.wikimedia.org`,
      project: project as string,
      path: `${a}/${ab}`,
      name: name as string,
      width: Number(width),
    };
  }
  const original = url.match(ORIGINAL);
  if (!original) return null;
  const [, host, project, a, ab, name] = original as unknown as string[];
  return {
    host: `${host}.wikimedia.org`,
    project: project as string,
    path: `${a}/${ab}`,
    name: name as string,
    width: null,
  };
}

// The image's thumbnail at a standard width; null when that isn't a photo Wikimedia thumbnails as itself.
export function wikimediaAt(image: WikimediaImage, width: (typeof WIKIMEDIA_WIDTHS)[number]): string | null {
  if (!PHOTO.test(image.name)) return null;
  return `https://${image.host}/wikipedia/${image.project}/thumb/${image.path}/${image.name}/${width}px-${image.name}`;
}

// The image at the smallest standard width at least `pixels` wide, never wider than the one at `url`
// (the size that was checked): what to load where it shows `pixels` wide. Null when `url` isn't
// Wikimedia's, or is already that size.
export function wikimediaSized(url: string, pixels: number): string | null {
  const image = wikimediaImage(url);
  if (!image) return null;
  const want = WIKIMEDIA_WIDTHS.find((w) => w >= pixels);
  if (!want || (image.width !== null && want >= image.width)) return null;
  return wikimediaAt(image, want);
}
